import { RefObject, useEffect, useState } from 'react';

const OVERSCAN = 8;

export function useVisibleRange(
  count: number,
  rowHeight: number,
  bodyRef: RefObject<HTMLTableSectionElement>
) {
  const [range, setRange] = useState(() => ({
    start: 0,
    end: Math.min(count, 30),
  }));

  useEffect(() => {
    let frame = 0;
    const update = () => {
      const body = bodyRef.current;
      if (!body || count === 0) {
        setRange((prev) => (prev.start === 0 && prev.end === 0 ? prev : { start: 0, end: 0 }));
        return;
      }
      // Viewport coordinates stay correct whether the window or a parent scrolls.
      const top = body.getBoundingClientRect().top;
      const start = Math.min(count, Math.max(0, Math.floor(-top / rowHeight) - OVERSCAN));
      const end = Math.min(count, Math.max(start, Math.ceil((window.innerHeight - top) / rowHeight) + OVERSCAN));
      setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    update();
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    const observer = new ResizeObserver(onScroll);
    observer.observe(document.body);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
      observer.disconnect();
    };
  }, [bodyRef, count, rowHeight]);

  return range;
}
